import { Injectable } from '@angular/core';
import { combineLatest, distinctUntilChanged } from 'rxjs/operators';

import { FileType } from '../../shared/models/file-item.model';
import { OperationStream } from '../../shared/models/operation-stream.model';
import { MruDataItem } from '../../shared/mru/mru-data-item.model';
import { MruDataServiceFactory } from '../../shared/mru/mru-data-service.factory';
import { MruData, MruDataService } from '../../shared/mru/mru-data.service';
import { PwaNativeBridgeService } from './pwa-native-bridge.service';

@Injectable()
export class PwaMruDataService implements MruDataService {
    private readonly cloudMruService: MruDataService;

    constructor(
        cloudMruServiceFactory: MruDataServiceFactory,
        private readonly pwaNativeBridgeService: PwaNativeBridgeService
    ) {
        this.cloudMruService = cloudMruServiceFactory.getMruService();
    }

    public getMruDocuments(types?: Set<FileType>): OperationStream<MruData> {
        const onlineMruStream = this.cloudMruService.getMruDocuments(types);
        const localMruObservable = this.pwaNativeBridgeService.getLocalMru(types);
        const compareByDate = (x, y) => y.LastAccessedTime - x.LastAccessedTime;
        const combineMru = (onlineMru: MruData, localMru: MruData) => {
            const items = localMru && localMru.items ? onlineMru.items.concat(localMru.items).sort(compareByDate) : onlineMru.items;
            return { ...onlineMru, items };
        };
        // Merge local mru and online mru
        const combinedResults = onlineMruStream.results.pipe(
            combineLatest(localMruObservable, combineMru),
            distinctUntilChanged((prev, curr) => prev.items &&
                curr.items &&
                prev.items.length &&
                prev.items.length === curr.items.length &&
                prev.items[0].WebUrl === curr.items[0].WebUrl &&
                prev.items[0].LastAccessedTime.getTime() === curr.items[0].LastAccessedTime.getTime() // Assumes pinning changes the access time
            ));
        return { results: combinedResults, initialCompletion: onlineMruStream.initialCompletion };
    }

    public updateMruDocuments(types?: Set<FileType>): Promise<void> {
        // This is used for updating the MRU after a cloud document is viewed or if the user manually requests for an update
        return this.cloudMruService.updateMruDocuments(types);
    }

    public removeDocument(item: MruDataItem): Promise<void> {
        if (item.IsLocal) {
            // TODO: Implement removal of local docs
            return Promise.resolve();
        }

        return this.cloudMruService.removeDocument(item);
    }

    public updatePinnedState(item: MruDataItem): Promise<void> {
        if (item.IsLocal) {
            // Pinned should not be called for local docs
            return Promise.resolve();
        }

        return this.cloudMruService.updatePinnedState(item);
    }

    public writeMruDocument(item: MruDataItem): Promise<void> {
        if (item.IsLocal) {
            // Not supported for local docs
            return Promise.resolve();
        }

        return this.cloudMruService.writeMruDocument(item);
    }
}
